import { Building, CreditCard, DollarSign, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';

export function PaymentLandscape() {
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [currentProcesses, setCurrentProcesses] = useState({
    invoiceVolume: '',
    averageInvoiceValue: '',
    paymentTerms: '',
    daysPayableOutstanding: '',
  });

  const paymentOptions = [
    { id: 'ach', label: 'ACH/Bank Transfer', icon: Building },
    { id: 'wire', label: 'Wire Transfer', icon: TrendingUp },
    { id: 'check', label: 'Paper Check', icon: DollarSign },
    { id: 'card', label: 'Credit/Debit Card', icon: CreditCard },
    { id: 'virtual-card', label: 'Virtual Card', icon: CreditCard },
    { id: 'other', label: 'Other', icon: DollarSign },
  ];

  const togglePaymentMethod = (methodId: string) => {
    setPaymentMethods((prev) =>
      prev.includes(methodId) ? prev.filter((id) => id !== methodId) : [...prev, methodId]
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Current Payment Methods
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Select all payment methods currently used by the prospect
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paymentOptions.map((option) => {
              const Icon = option.icon;
              const id = `payment-${option.id}`;
              return (
                <label
                  key={option.id}
                  htmlFor={id}
                  className={`flex items-center space-x-3 p-4 border-2 transition-colors cursor-pointer ${
                    paymentMethods.includes(option.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <Checkbox
                    id={id}
                    checked={paymentMethods.includes(option.id)}
                    onChange={() => togglePaymentMethod(option.id)}
                  />
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">{option.label}</span>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Payment Processing Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoiceVolume">Monthly Invoice Volume</Label>
              <Input
                id="invoiceVolume"
                placeholder="e.g., 150 invoices"
                value={currentProcesses.invoiceVolume}
                onChange={(e) =>
                  setCurrentProcesses((prev) => ({ ...prev, invoiceVolume: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="averageInvoiceValue">Average Invoice Value</Label>
              <Input
                id="averageInvoiceValue"
                placeholder="e.g., $5,000"
                value={currentProcesses.averageInvoiceValue}
                onChange={(e) =>
                  setCurrentProcesses((prev) => ({ ...prev, averageInvoiceValue: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="paymentTerms">Standard Payment Terms</Label>
              <Select
                onValueChange={(value) =>
                  setCurrentProcesses((prev) => ({ ...prev, paymentTerms: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select terms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="net-15">Net 15</SelectItem>
                  <SelectItem value="net-30">Net 30</SelectItem>
                  <SelectItem value="net-45">Net 45</SelectItem>
                  <SelectItem value="net-60">Net 60</SelectItem>
                  <SelectItem value="due-on-receipt">Due on Receipt</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dpo">Days Payable Outstanding (DPO)</Label>
              <Input
                id="dpo"
                placeholder="e.g., 32 days"
                value={currentProcesses.daysPayableOutstanding}
                onChange={(e) =>
                  setCurrentProcesses((prev) => ({
                    ...prev,
                    daysPayableOutstanding: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Payment Challenges</CardTitle>
          <p className="text-sm text-muted-foreground">
            Describe any current pain points with payment processing
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="challenges">Challenges & Pain Points</Label>
            <Textarea
              id="challenges"
              placeholder="e.g., Manual check processing, delayed payments, lack of visibility..."
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="goals">Payment Processing Goals</Label>
            <Textarea
              id="goals"
              placeholder="e.g., Reduce processing time, improve cash flow, automate reconciliation..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Technology Stack</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="erp">ERP System</Label>
              <Input id="erp" placeholder="e.g., SAP, Oracle, QuickBooks" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="banking">Banking Platform</Label>
              <Input id="banking" placeholder="e.g., JP Morgan, Bank of America" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ap">AP Automation Tool</Label>
              <Input id="ap" placeholder="e.g., None, Bill.com, AvidXchange" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="treasury">Treasury Management</Label>
              <Input id="treasury" placeholder="e.g., Manual, TMS platform" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
